import {
    Component,
    forwardRef,
    input,
    InputSignal,
    ViewChild,
    ElementRef,
    effect,
    CUSTOM_ELEMENTS_SCHEMA,
    signal,
    WritableSignal,
    inject,
    OnDestroy,
    output,
    OutputEmitterRef
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { UserService } from '../../services/user.service';
import { IUser, Roles } from '@shs-digi-care-monorepo/interfaces';
import { Subject, debounceTime, distinctUntilChanged, switchMap, of, catchError, tap, finalize, filter } from 'rxjs';
import { CommonModule } from '@angular/common';

/**
 * User picker component with search functionality.
 * Exposes user GID as value and provides editable dropdown with user search.
 */
@Component({
    selector: 'sh-user-picker',
    templateUrl: './sh-user-picker.component.html',
    standalone: true,
    imports: [CommonModule],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ShUserPickerComponent),
            multi: true
        }
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ShUserPickerComponent implements ControlValueAccessor, OnDestroy {
    @ViewChild('dropdown', { static: false }) public dropdownElement: ElementRef<HTMLElement> | undefined;

    private readonly userService = inject(UserService);

    // Inputs
    public label: InputSignal<string> = input<string>('User');
    public roles: InputSignal<Roles[] | null> = input<Roles[] | null>(null);
    public teamId: InputSignal<string | null> = input<string | null>(null);
    public disabled: InputSignal<boolean> = input<boolean>(false);
    public mandatory: InputSignal<boolean> = input<boolean>(false);
    public error: InputSignal<boolean> = input<boolean>(false);
    public placeholder: InputSignal<string> = input<string>('Search for user...');
    public optionTestId: InputSignal<string | undefined> = input<string | undefined>(undefined);
    public pageSize: InputSignal<number> = input<number>(10);

    // Outputs
    public userSelected: OutputEmitterRef<IUser | null> = output<IUser | null>();

    // Internal state
    protected currentValue: string | null = null; // GID
    protected selectedUser: IUser | null = null;
    protected searchResults: WritableSignal<IUser[]> = signal([]);
    protected isLoading: WritableSignal<boolean> = signal(false);
    protected isLoadingValue: WritableSignal<boolean> = signal(false);
    protected isDisabled: WritableSignal<boolean> = signal(false);
    protected searchTerm: WritableSignal<string> = signal('');
    protected isSearching: WritableSignal<boolean> = signal(false);

    private searchSubject = new Subject<string>();
    private isFirstTeamIdEffect = true;

    /**
     * Tracks the most recent value we propagated upwards via `onChange`. While
     * set, any incoming `writeValue` call whose value does not match is
     * treated as a stale echo and ignored. This prevents the picker from
     * "snapping back" to a previously-selected user when the parent form is
     * slow to acknowledge a local clear/select action.
     */
    private pendingValue: string | null | undefined = undefined;

    private onChange: (value: string | null) => void = () => {};

    private onTouched: () => void = () => {};

    constructor() {
        // Set up search with debounce
        this.searchSubject
            .pipe(
                debounceTime(300),
                distinctUntilChanged(),
                switchMap((term) => {
                    this.isLoading.set(true);
                    const trimmed = term.trim();
                    const searchTerm = trimmed.length > 0 ? trimmed : undefined;
                    return this.userService
                        .getUsers(0, this.pageSize(), searchTerm, this.roles() ?? undefined, this.teamId() ?? undefined)
                        .pipe(
                            catchError((error) => {
                                console.error('Error searching users:', error);
                                return of({ data: [], total: 0, page: 0, pageSize: 0 });
                            }),
                            finalize(() => this.isLoading.set(false))
                        );
                }),
                tap((result) => {
                    this.searchResults.set(result.data || []);
                })
            )
            .subscribe();

        // Load selected user when value changes
        effect(() => {
            if (this.currentValue && !this.selectedUser) {
                this.loadUserByGid(this.currentValue);
            }
        });

        // Reset selection and load initial page when teamId changes
        effect(() => {
            const _teamId = this.teamId();
            // Skip the initial run so we don't clobber a value provided by
            // the parent form control via writeValue().
            if (!this.isFirstTeamIdEffect) {
                this.handleClear();
            }
            this.isFirstTeamIdEffect = false;
            this.loadInitialPage();
        });
    }

    // ControlValueAccessor implementation
    public writeValue(value: string | null): void {
        // Don't overwrite state while the user is actively searching.
        if (this.isSearching()) {
            return;
        }
        // If we have a pending local change, only accept the matching ack;
        // ignore stale echoes that would otherwise snap the picker back to
        // a previously-selected user.
        if (this.pendingValue !== undefined) {
            if (value === this.pendingValue) {
                this.pendingValue = undefined;
            } else {
                return;
            }
        }
        if (value === this.currentValue) {
            return;
        }
        this.currentValue = value;
        if (value) {
            this.loadUserByGid(value);
        } else {
            this.selectedUser = null;
        }
    }

    public registerOnChange(fn: (value: string | null) => void): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.isDisabled.set(isDisabled);
    }

    /**
     * Load the first page of results without a search term.
     */
    private loadInitialPage(): void {
        this.isLoading.set(true);
        this.userService
            .getUsers(0, this.pageSize(), undefined, this.roles() ?? undefined, this.teamId() ?? undefined)
            .pipe(
                tap((result) => {
                    this.searchResults.set(result.data || []);
                }),
                catchError((error) => {
                    console.error('Error loading initial users:', error);
                    return of({ data: [], total: 0, page: 0, pageSize: 0 });
                }),
                finalize(() => this.isLoading.set(false))
            )
            .subscribe();
    }

    /**
     * Handle search input change.
     *
     * The picker behaves as a free-text autocomplete: while editing, the
     * selected user is not re-rendered as an active item, so the input value
     * is fully under the user's control.
     */
    protected handleSearchInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        const term = input.value;
        const wasSearching = this.isSearching();
        this.searchTerm.set(term);
        this.isSearching.set(true);
        this.searchSubject.next(term);
        // When the user starts editing, make sure there are suggestions to
        // choose from even if they previously had a selected user (which would
        // have left `searchResults` stale or empty).
        if (!wasSearching && term.trim().length === 0) {
            this.loadInitialPage();
        }
    }

    /**
     * Called when the dropdown's `active` property changes. The dropdown
     * fires this on itself when its overlay opens or closes (and menu-items
     * also bubble their own `active-changed` events through here, which we
     * ignore via the `event.target` check).
     *
     * When the dropdown closes while the user was editing without committing
     * a valid selection, revert the input back to the previously selected
     * user – standard autocomplete UX.
     */
    protected handleActiveChanged(event: Event): void {
        const dropdown = this.dropdownElement?.nativeElement as (HTMLElement & { active?: boolean }) | undefined;
        if (!dropdown || event.target !== dropdown) {
            return;
        }
        if (dropdown.active) {
            return;
        }
        if (!this.isSearching()) {
            return;
        }
        this.searchTerm.set('');
        this.isSearching.set(false);
        this.setDropdownDisplayValue(this.selectedUser ? this.getUserDisplayName(this.selectedUser) : '');
        if (this.selectedUser) {
            this.loadInitialPage();
        }
    }

    /**
     * Imperatively set the text shown in the SHUI dropdown's input.
     */
    private setDropdownDisplayValue(text: string): void {
        const el = this.dropdownElement?.nativeElement as (HTMLElement & { value?: string }) | undefined;
        if (el) {
            el.value = text;
        }
    }

    /**
     * Handle user selection from dropdown.
     *
     * Keeps the previously-fetched `searchResults` in memory so the dropdown
     * still shows useful alternatives the next time it is opened, without
     * forcing the user to type again.
     */
    protected handleUserSelect(user: IUser): void {
        this.currentValue = user.gid;
        this.selectedUser = user;
        this.searchTerm.set('');
        this.isSearching.set(false);
        this.pendingValue = user.gid;
        this.onChange(user.gid);
        this.onTouched();
        this.userSelected.emit(user);
    }

    /**
     * Handle clear selection.
     *
     * Reloads the initial page so the dropdown immediately offers other users
     * to pick from after the clear, instead of going empty.
     */
    protected handleClear(): void {
        this.currentValue = null;
        this.selectedUser = null;
        this.searchTerm.set('');
        this.isSearching.set(false);
        this.pendingValue = null;
        this.onChange(null);
        this.onTouched();
        this.userSelected.emit(null);
        this.loadInitialPage();
    }

    /**
     * Load user details by GID
     */
    private loadUserByGid(gid: string): void {
        this.isLoadingValue.set(true);
        this.userService
            .getByGID(gid)
            .pipe(
                filter((user): user is IUser => !!user),
                tap((user) => {
                    this.selectedUser = user;
                    this.userSelected.emit(user);
                }),
                catchError((error) => {
                    console.error('Error loading user:', error);
                    this.selectedUser = null;
                    return of(null);
                }),
                finalize(() => this.isLoadingValue.set(false))
            )
            .subscribe();
    }

    /**
     * Get display name for a user
     */
    protected getUserDisplayName(user: IUser | null): string {
        if (!user) return '';
        return `${user.firstName} ${user.lastName}`.trim() || user.email || user.gid;
    }

    /**
     * Check if a user is currently selected
     */
    protected isUserActive(userGid: string): boolean {
        return this.currentValue === userGid;
    }

    public ngOnDestroy(): void {
        this.searchSubject.complete();
    }
}
